import { useState, type ReactNode } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import * as ToastPrimitive from '@radix-ui/react-toast'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Button, IconButton } from './Button'
import { Input } from './Field'

/**
 * Modal, drawer and bottom sheet are one component with three placements.
 * Radix handles the focus trap, focus restore and Esc-to-close required by
 * §15.4; this wrapper supplies the placement and the design-system chrome.
 */
export type OverlayPlacement = 'center' | 'right' | 'bottom'

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  placement = 'center',
  footer,
  children,
  className,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  placement?: OverlayPlacement
  footer?: ReactNode
  children: ReactNode
  className?: string
}) {
  const { t } = useTranslation()

  const panel =
    placement === 'center'
      ? 'left-1/2 top-1/2 w-[min(560px,calc(100vw-32px))] max-h-[85vh] -translate-x-1/2 -translate-y-1/2 rounded-lg'
      : placement === 'right'
        ? 'right-0 top-0 h-full w-[min(420px,100vw)] rounded-l-lg'
        : 'bottom-0 left-0 max-h-[92vh] w-full rounded-t-lg'

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-itpu-navy/50 animate-fade-in" />
        <DialogPrimitive.Content
          className={cn(
            'fixed z-50 flex flex-col overflow-hidden border border-border bg-surface shadow-e3 animate-slide-up',
            panel,
            className,
          )}
        >
          <header className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
            <div className="min-w-0">
              <DialogPrimitive.Title className="text-h2">{title}</DialogPrimitive.Title>
              {description && (
                <DialogPrimitive.Description className="mt-1 text-meta text-text-muted">
                  {description}
                </DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close asChild>
              <IconButton label={t('action.close')} size="sm">
                <X className="size-4" />
              </IconButton>
            </DialogPrimitive.Close>
          </header>
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && (
            <footer className="flex flex-wrap justify-end gap-2 border-t border-border px-5 py-4">
              {footer}
            </footer>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

/**
 * §11.6 — the destructive variant requires typing the exact name before the
 * confirm button enables, so an irreversible delete cannot be a slip.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  confirmPhrase,
  confirmPrompt,
  destructive = false,
  reasonLabel,
  onConfirm,
  busy = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  body: ReactNode
  confirmLabel: string
  confirmPhrase?: string
  confirmPrompt?: string
  destructive?: boolean
  reasonLabel?: string
  onConfirm: (reason: string) => void
  busy?: boolean
}) {
  const { t } = useTranslation()
  const [typed, setTyped] = useState('')
  const [reason, setReason] = useState('')

  const phraseOk = !confirmPhrase || typed.trim() === confirmPhrase.trim()
  const reasonOk = !reasonLabel || reason.trim().length > 0

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setTyped('')
          setReason('')
        }
        onOpenChange(next)
      }}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('action.cancel')}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            disabled={!phraseOk || !reasonOk}
            loading={busy}
            onClick={() => onConfirm(reason.trim())}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="text-body text-text-muted">{body}</div>
        {confirmPhrase && (
          <Input
            label={confirmPrompt}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
          />
        )}
        {reasonLabel && (
          <Input label={reasonLabel} value={reason} onChange={(e) => setReason(e.target.value)} required />
        )}
      </div>
    </Modal>
  )
}

export function Tooltip({ content, children }: { content: ReactNode; children: ReactNode }) {
  return (
    <TooltipPrimitive.Root delayDuration={200}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          sideOffset={6}
          className="z-50 max-w-64 rounded-sm border border-border bg-surface px-3 py-2 text-meta text-text shadow-e2"
        >
          {content}
          <TooltipPrimitive.Arrow className="fill-surface" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}

export const TooltipProvider = TooltipPrimitive.Provider

export function Tabs({
  value,
  onValueChange,
  tabs,
  className,
}: {
  value: string
  onValueChange: (value: string) => void
  tabs: Array<{ value: string; label: ReactNode; content: ReactNode }>
  className?: string
}) {
  return (
    <TabsPrimitive.Root value={value} onValueChange={onValueChange} className={className}>
      <TabsPrimitive.List className="flex flex-wrap gap-1 border-b border-border">
        {tabs.map((tab) => (
          <TabsPrimitive.Trigger
            key={tab.value}
            value={tab.value}
            className={cn(
              'min-w-max rounded-t-sm px-4 py-2 text-body text-text-muted transition-colors',
              'hover:text-text data-[state=active]:border-b-2 data-[state=active]:border-itpu-teal',
              'data-[state=active]:font-medium data-[state=active]:text-text',
            )}
          >
            {tab.label}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
      {tabs.map((tab) => (
        <TabsPrimitive.Content key={tab.value} value={tab.value} className="pt-6">
          {tab.content}
        </TabsPrimitive.Content>
      ))}
    </TabsPrimitive.Root>
  )
}

export interface ToastMessage {
  id: number
  title: string
  tone?: 'neutral' | 'success' | 'danger'
}

export function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastMessage[]
  onDismiss: (id: number) => void
}) {
  return (
    <ToastPrimitive.Provider swipeDirection="right" duration={5000}>
      {toasts.map((toast) => (
        <ToastPrimitive.Root
          key={toast.id}
          open
          onOpenChange={(open) => {
            if (!open) onDismiss(toast.id)
          }}
          className={cn(
            'card flex items-center gap-3 px-4 py-3 shadow-e2 animate-slide-up',
            toast.tone === 'success' && 'border-success/40 bg-success-tint',
            toast.tone === 'danger' && 'border-danger/40 bg-danger-tint',
          )}
        >
          <ToastPrimitive.Title className="text-body">{toast.title}</ToastPrimitive.Title>
        </ToastPrimitive.Root>
      ))}
      <ToastPrimitive.Viewport className="fixed bottom-4 right-4 z-[60] flex w-[min(380px,calc(100vw-32px))] flex-col gap-2 outline-none" />
    </ToastPrimitive.Provider>
  )
}
